import React from "react";

import {
  IgButton,
  IgDialog,
  IgLink,
  IgTypography,
} from "@intelligems/ig-theme";
import { FileExportType, GenericExperienceFe } from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import { useFileExportExperience } from "@src/api/analytics/file-export-experience";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { useDialog } from "@src/hooks/dialog";
import { useSelector } from "@src/store";
import { dataExport } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface DataExportDialogProps {
  experience: GenericExperienceFe;
  showDataReconciliationExport?: boolean;
}

export const DataExportDialog = ({
  showDataReconciliationExport,
  experience,
}: DataExportDialogProps) => {
  const dialog = useDialog("dataExport");
  const user = useLoggedInUser();
  const fileExport = useFileExportExperience({
    experience,
    graphConfigs: [],
    overviewDataLoaded: true,
  });
  const isAdmin = useIsAdmin();

  const isLoading = fileExport.isPending;
  const experienceId = useSelector(
    (state) => state.dashboardFilters.experienceId
  );

  const handleExperimentDownload = (value: FileExportType) => {
    if (experienceId && user.isSuccess) {
      fileExport.mutate(value);
      dialog.close();
    }
  };

  return (
    <IgDialog
      title={"Export Your Data"}
      handleClose={dialog.close}
      handleYesClick={dialog.close}
      showCloseIcon={true}
      showDialog={dialog.isOpen}
      dialogHeight={null}
      hideActions={true}
      subtitle={
        <IgTypography>
          Choose an option below and we will provide a downloadable link at the
          top of the screen. Learn more about exporting your data{" "}
          <IgTypography>
            <IgLink
              onClick={() => openNewTab(dataExport, "Experiment Data Export")}
            >
              here.
            </IgLink>
          </IgTypography>
        </IgTypography>
      }
      dontUseDialogContent={true}
      rawContent={
        <Grid container direction={"row"} columnSpacing={2}>
          <Grid>
            <IgButton
              loading={isLoading}
              variant={"outlined"}
              onClick={() => {
                handleExperimentDownload("order_line_items");
              }}
            >
              Line Items
            </IgButton>
          </Grid>
          <Grid>
            <IgButton
              loading={isLoading}
              variant={"outlined"}
              onClick={() => {
                handleExperimentDownload("orders");
              }}
            >
              Orders
            </IgButton>
          </Grid>
          {showDataReconciliationExport || isAdmin ? (
            <Grid>
              <IgButton
                loading={isLoading}
                variant={"outlined"}
                onClick={() => {
                  handleExperimentDownload("order_reconciliation");
                }}
              >
                Order Reconciliation
              </IgButton>
            </Grid>
          ) : null}
        </Grid>
      }
      loading={isLoading}
    />
  );
};
